import { useState, useEffect } from 'react';
import { Table } from '@/UIlibrary/Table/Table';
import { GRAY_COLORS, BRAND_COLORS } from '@/UIlibrary/globalTheme';
import { useTranslations } from 'use-intl';
import { ChevronDown, ChevronUp, AlertCircle, Search, Calendar, X } from 'react-feather';
import { Typography } from '@/stories/Library/Components/Typography/Typography';
import { useNavigate } from 'react-router-dom';
import Button from '@/UIlibrary/Button/Button';
import InputField from '@/UIlibrary/InputField/InputField';
import { useApolloClient } from '@/providers/MultiApolloProvider';
import { SCHEMAS } from '@/apolloClient';
import { formatDateTime } from '@/utils/utils';
import { DateRange } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { es } from 'date-fns/locale';
import { RequestStatusBadge } from './RequestStatusBadge';
import MultiSelectMicrocreditRequestStatus from './MultiSelectCreditRequestStatuses';
import {
	useMicrocreditRequestsQuery,
	SortDirection,
	MicrocreditRequestSort,
	MicrocreditRequestFilter,
	OnboardingStatus,
	MicrocreditRequestStatus
} from '@/graphql/__generated__/credit-requests';
import MultiSelectOnboardingStatus from '@/pages/Onboarding/MultiSelectOnboardingStatus';
import { OnboardingStatusBadge } from '@/pages/Onboarding/OnboardingStatusBadge';

type SortableField = 'startedAt' | 'status';

const PAGE_SIZE = 15;

const MicrocreditRequestsTable = () => {
	const t = useTranslations('MicrocreditRequests');
	const navigate = useNavigate();
	const microcreditsClient = useApolloClient(SCHEMAS.MICROCREDITS);
	const [sortDirection, setSortDirection] = useState<SortDirection>(SortDirection.DESC);
	const [sortField, setSortField] = useState<SortableField>('startedAt');
	const [searchInput, setSearchInput] = useState('');
	const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
	const [selectedStatuses, setSelectedStatuses] = useState<MicrocreditRequestStatus[]>([]);
	const [selectedOnboardingStatuses, setSelectedOnboardingStatuses] = useState<OnboardingStatus[]>([
		OnboardingStatus.APPROVED
	]);
	const [showDatePicker, setShowDatePicker] = useState(false);
	const [dateRange, setDateRange] = useState<{
		startDate: Date | undefined;
		endDate: Date | undefined;
		key: string;
	}>({
		startDate: undefined,
		endDate: undefined,
		key: 'selection'
	});

	// Debounce search term to avoid too many API calls
	useEffect(() => {
		const timerId = setTimeout(() => {
			setDebouncedSearchTerm(searchInput);
		}, 300);

		return () => clearTimeout(timerId);
	}, [searchInput]);

	const getFilter = (): MicrocreditRequestFilter | undefined => {
		const filter: MicrocreditRequestFilter = {};

		if (debouncedSearchTerm) {
			filter.searchTerm = debouncedSearchTerm;
		}

		if (selectedStatuses.length > 0) {
			filter.microcreditRequestStatus = selectedStatuses;
		}

		if (selectedOnboardingStatuses.length > 0) {
			filter.onboardingStatus = selectedOnboardingStatuses;
		}

		if (dateRange.startDate || dateRange.endDate) {
			filter.startedAt = {};

			if (dateRange.startDate) {
				// Set start date to beginning of the day (00:00:00)
				const startDate = new Date(dateRange.startDate);
				startDate.setHours(0, 0, 0, 0);
				filter.startedAt.from = startDate.toISOString();
			}

			if (dateRange.endDate) {
				// Set end date to end of the day (23:59:59.999)
				const endDate = new Date(dateRange.endDate);
				endDate.setHours(23, 59, 59, 999);
				filter.startedAt.to = endDate.toISOString();
			}
		}

		// Return undefined if no filters applied
		return Object.keys(filter).length ? filter : undefined;
	};

	const getCurrentSort = (): MicrocreditRequestSort => {
		// Map UI sort fields to API sort fields (if different)
		const sortMapping: Record<SortableField, string> = {
			startedAt: 'startedAt',
			status: 'microcreditRequestStatus'
		};

		return {
			[sortMapping[sortField]]: sortDirection
		};
	};

	// Using the query with filter and sort support
	const { loading, error, data, fetchMore, refetch } = useMicrocreditRequestsQuery({
		client: microcreditsClient,
		variables: {
			first: PAGE_SIZE,
			sorts: [getCurrentSort()],
			filter: getFilter()
		},
		notifyOnNetworkStatusChange: true
	});

	// Effect to refetch when filters change
	useEffect(() => {
		refetch({
			first: PAGE_SIZE,
			sorts: [getCurrentSort()],
			filter: getFilter()
		});
	}, [
		debouncedSearchTerm,
		sortDirection,
		sortField,
		selectedStatuses,
		selectedOnboardingStatuses,
		dateRange,
		refetch
	]);

	const handleLoadMore = async () => {
		if (!data?.microcreditRequests?.pageInfo.hasNextPage) return;

		const lastCursor = data.microcreditRequests?.pageInfo.endCursor;
		if (!lastCursor) return;

		try {
			await fetchMore({
				variables: {
					after: lastCursor,
					first: PAGE_SIZE,
					sorts: [getCurrentSort()],
					filter: getFilter()
				},
				updateQuery: (prev, { fetchMoreResult }) => {
					if (!fetchMoreResult?.microcreditRequests) return prev;

					return {
						...prev,
						microcreditRequests: {
							...fetchMoreResult.microcreditRequests,
							edges: [
								...(prev.microcreditRequests?.edges || []),
								...(fetchMoreResult.microcreditRequests.edges || [])
							]
						}
					};
				}
			});
		} catch (error) {
			console.error('Error loading more microcredit requests:', error);
		}
	};

	const handleDateRangeChange = (
		rangesByKey: Record<string, { startDate?: Date; endDate?: Date }>
	) => {
		const selection = rangesByKey.selection;
		if (selection?.startDate && selection?.endDate) {
			setDateRange({
				startDate: selection.startDate,
				endDate: selection.endDate,
				key: 'selection'
			});
			setShowDatePicker(false);
		}
	};

	const handleClearDateRange = () => {
		setDateRange({
			startDate: undefined,
			endDate: undefined,
			key: 'selection'
		});
	};

	const handleSortChange = (field: SortableField) => {
		if (field === sortField) {
			setSortDirection(
				sortDirection === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC
			);
		} else {
			setSortField(field);
			setSortDirection(SortDirection.DESC);
		}
	};

	const handleSearchSubmit = () => {
		// The debounce effect will handle the actual search
		console.log('Search term:', searchInput);
	};

	const handleClearSearch = () => {
		setSearchInput('');
		setDebouncedSearchTerm('');
	};

	const handleStatusFilterChange = (statuses: MicrocreditRequestStatus[]) => {
		setSelectedStatuses(statuses);
	};

	const handleOnboardingStatusFilterChange = (status: OnboardingStatus[]) => {
		setSelectedOnboardingStatuses(status);
	};

	const handleClearAllFilters = () => {
		setSearchInput('');
		setDebouncedSearchTerm('');
		setSelectedStatuses([]);
		setSelectedOnboardingStatuses([]);
		setDateRange({
			startDate: undefined,
			endDate: undefined,
			key: 'selection'
		});
	};

	const handleViewDetails = (microcreditRequestId: string) => {
		navigate(`requests/${microcreditRequestId}`);
	};

	const SortIcon = ({ field }: { field: SortableField }) => {
		if (field !== sortField) return null;
		const Icon = sortDirection === SortDirection.ASC ? ChevronUp : ChevronDown;
		return <Icon size={16} className="text-gray-500" />;
	};

	const renderMicrocreditRequestStatus = (status: MicrocreditRequestStatus) => (
		<RequestStatusBadge status={status} size="sm" />
	);

	const renderOnboardingStatus = (request: any) => (
		<OnboardingStatusBadge status={request.onboardingStatus as OnboardingStatus} size="sm" />
	);

	const renderFullName = (request: {
		names: string;
		firstLastName: string;
		secondLastName: string;
	}) => (
		<Typography size="sm" color={GRAY_COLORS.gray700}>
			{[request.names, request.firstLastName, request.secondLastName]
				.filter(Boolean)
				.join(' ')
				.trim() || '-'}
		</Typography>
	);

	const renderEmail = (request: { email: string }) => (
		<Typography size="sm" color={GRAY_COLORS.gray700}>
			{request.email || '-'}
		</Typography>
	);

	const renderPhoneNumber = (request: { phoneNumber: string }) => (
		<Typography size="sm" color={GRAY_COLORS.gray700}>
			{request.phoneNumber || '-'}
		</Typography>
	);

	const renderDate = (date: string) => (
		<Typography size="sm" color={GRAY_COLORS.gray700}>
			{formatDateTime(date) || '-'}
		</Typography>
	);

	const renderAmount = (details: { amountRequested: string }) => {
		const amount = details?.amountRequested;
		return (
			<Typography size="sm" color={GRAY_COLORS.gray700}>
				{amount || '-'}
			</Typography>
		);
	};

	const renderViewDetailsButton = (request: { id: string }) => (
		<Button textOnly onClick={() => handleViewDetails(request.id)} label={t('ViewDetails')} />
	);

	const getRequestStatusDisplayName = (status: MicrocreditRequestStatus) => {
		const statusMap = {
			[MicrocreditRequestStatus.CREATED]: t('CreatedStatus'),
			[MicrocreditRequestStatus.PERSONAL_INFORMATION_REGISTERED]: t('PersonalInformationStatus'),
			[MicrocreditRequestStatus.ACTIVITY_INFORMATION_REGISTERED]: t('BusinessInformationStatus'),
			[MicrocreditRequestStatus.CREDIT_DETAILS_REGISTERED]: t('CreditDetailsStatus'),
			[MicrocreditRequestStatus.MONTHLY_INCOME_INFORMATION_REGISTERED]:
				t('IncomeInformationStatus'),
			[MicrocreditRequestStatus.MONTHLY_BUSINESS_EXPENSES_REGISTERED]: t('BusinessExpensesStatus'),
			[MicrocreditRequestStatus.MONTHLY_HOUSE_EXPENSES_REGISTERED]: t('HouseExpensesStatus'),
			[MicrocreditRequestStatus.VALUE_OF_ASSETS_REGISTERED]: t('ValueOfAssetsStatus'),
			[MicrocreditRequestStatus.OTHER_CREDITS_REGISTERED]: t('OtherCreditsStatus'),
			[MicrocreditRequestStatus.IN_REVIEW]: t('InReviewStatus'),
			[MicrocreditRequestStatus.REJECTED]: t('RejectedStatus'),
			[MicrocreditRequestStatus.COMPLETED]: t('CompletedStatus'),
			[MicrocreditRequestStatus.PASSED_TO_CREDIT_COMMITTEE]: t('PassedToCommitteeStatus'),
			[MicrocreditRequestStatus.APPROVED_BY_CREDIT_COMMITTEE]: t('ApprovedByCommitteeStatus'),
			[MicrocreditRequestStatus.REJECTED_BY_CREDIT_COMMITTEE]: t('RejectedByCommitteeStatus')
		};

		return statusMap[status] || t('Status');
	};

	const getOnboardingStatusText = (status: OnboardingStatus): string => {
		const statusMap: Record<OnboardingStatus, string> = {
			[OnboardingStatus.IN_PROCESS]: t('InProcess'),
			[OnboardingStatus.CORRECTIONS_REQUESTED]: t('CorrectionsRequested'),
			[OnboardingStatus.PENDING_REVIEW]: t('PendingReview'),
			[OnboardingStatus.REJECTED]: t('Rejected'),
			[OnboardingStatus.APPROVED]: t('Approved')
		};
		return statusMap[status] || t('InProcess');
	};

	const columns = [
		{
			header: '',
			key: 'actions',
			cellRenderer: (_: any, request: any) => renderViewDetailsButton(request)
		},
		{
			header: t('Name'),
			key: 'names',
			cellRenderer: (_: any, request: any) => renderFullName(request)
		},
		{
			header: t('Email'),
			key: 'email',
			cellRenderer: (_: any, request: any) => renderEmail(request)
		},
		{
			header: t('PhoneNumber'),
			key: 'phoneNumber',
			cellRenderer: (_: any, request: any) => renderPhoneNumber(request)
		},
		{
			header: t('AmountRequested'),
			key: 'creditDetails',
			cellRenderer: (_: any, request: { creditDetails: any }) => renderAmount(request.creditDetails)
		},
		{
			header: (
				<div
					className="flex items-center gap-2 cursor-pointer"
					onClick={() => handleSortChange('startedAt')}
					role="button"
					tabIndex={0}
				>
					<span>{t('StartedAt')}</span>
					<SortIcon field="startedAt" />
				</div>
			),
			key: 'startedAt',
			cellRenderer: (_: any, request: any) => renderDate(request.startedAt || request.insertedAt)
		},
		{
			header: (
				<div className="flex items-center gap-2 cursor-pointer" tabIndex={0}>
					<span>{t('OnboardingStatus')}</span>
				</div>
			),
			key: 'onboardingStatus',
			cellRenderer: (_: any, request: any) => renderOnboardingStatus(request)
		},
		{
			header: (
				<div
					className="flex items-center gap-2 cursor-pointer"
					onClick={() => handleSortChange('status')}
					role="button"
					tabIndex={0}
				>
					<span>{t('MicrocreditStatus')}</span>
					<SortIcon field="status" />
				</div>
			),
			key: 'status',
			cellRenderer: (_: any, request: any) =>
				renderMicrocreditRequestStatus(request.microcreditRequestStatus)
		}
	];

	if (error) {
		return (
			<div className="flex items-center gap-2 p-4 text-red-600 bg-red-50 rounded-md">
				<AlertCircle size={16} />
				<Typography size="sm">{t('ErrorLoadingMicrocreditRequests')}</Typography>
			</div>
		);
	}

	const isInitialLoading = loading && !data;
	const microcreditRequests =
		data?.microcreditRequests?.edges
			?.map((edge) => edge?.node)
			.filter((node) => node !== null && node !== undefined) || [];
	const totalCount = data?.microcreditRequests?.count || 0;
	const hasMore = data?.microcreditRequests?.pageInfo.hasNextPage || false;

	// Define search action buttons based on whether search is active
	const searchActions = searchInput ? (
		<div className="flex items-center">
			<Button
				label={''}
				endIcon={<X size={18} />}
				onClick={handleClearSearch}
				textOnly
				size="auto"
				data-testid="clear-search-button"
			/>
		</div>
	) : (
		<Button
			label={''}
			textOnly
			onClick={handleSearchSubmit}
			data-testid="search-button"
			endIcon={<Search size={20} />}
		/>
	);

	const MicrocreditRequestStatusCompletionOrder = [
		MicrocreditRequestStatus.CREATED,
		MicrocreditRequestStatus.PERSONAL_INFORMATION_REGISTERED,
		MicrocreditRequestStatus.ACTIVITY_INFORMATION_REGISTERED,
		MicrocreditRequestStatus.CREDIT_DETAILS_REGISTERED,
		MicrocreditRequestStatus.MONTHLY_INCOME_INFORMATION_REGISTERED,
		MicrocreditRequestStatus.MONTHLY_BUSINESS_EXPENSES_REGISTERED,
		MicrocreditRequestStatus.MONTHLY_HOUSE_EXPENSES_REGISTERED,
		MicrocreditRequestStatus.VALUE_OF_ASSETS_REGISTERED,
		MicrocreditRequestStatus.OTHER_CREDITS_REGISTERED,
		MicrocreditRequestStatus.IN_REVIEW,
		MicrocreditRequestStatus.REJECTED,
		MicrocreditRequestStatus.COMPLETED,
		MicrocreditRequestStatus.PASSED_TO_CREDIT_COMMITTEE,
		MicrocreditRequestStatus.APPROVED_BY_CREDIT_COMMITTEE,
		MicrocreditRequestStatus.REJECTED_BY_CREDIT_COMMITTEE
	];

	const OnboardingStatusCompletionOrder = [
		OnboardingStatus.IN_PROCESS,
		OnboardingStatus.CORRECTIONS_REQUESTED,
		OnboardingStatus.PENDING_REVIEW,
		OnboardingStatus.REJECTED,
		OnboardingStatus.APPROVED
	];

	const statusOptions = MicrocreditRequestStatusCompletionOrder.map((status) => ({
		value: status,
		label: `${getRequestStatusDisplayName(status)}`
	}));

	const onboardingStatusOptions = OnboardingStatusCompletionOrder.map((status) => ({
		value: status,
		label: `${getOnboardingStatusText(status)}`
	}));

	return (
		<div className="flex flex-col py-4 gap-4">
			{/* Search and filter section */}
			<div className="flex flex-col md:flex-row w-full items-center gap-8">
				<div className="flex gap-4 mt-5 w-full md:w-auto">
					<InputField
						onChange={(e) => setSearchInput(e.target.value)}
						value={searchInput}
						shape="pill"
						placeholder={t('Search')}
						dataTestId="searchInput"
						endAction={searchActions}
					/>
				</div>

				<div className="flex gap-4 items-center w-full md:w-auto">
					<MultiSelectOnboardingStatus
						selectedStatuses={selectedOnboardingStatuses}
						onChange={handleOnboardingStatusFilterChange}
						statusOptions={onboardingStatusOptions}
						placeholder={t('SelectAnOption')}
						label={t('SelectOnboardingStatuses')}
					/>
				</div>
				<div className="flex gap-4 items-center w-full md:w-auto">
					<MultiSelectMicrocreditRequestStatus
						selectedStatuses={selectedStatuses}
						onChange={handleStatusFilterChange}
						statusOptions={statusOptions}
						placeholder={t('SelectAnOption')}
						label={t('SelectMicrocreditStatuses')}
					/>
				</div>
			</div>

			{/* Date picker section */}
			<div className="flex flex-wrap gap-2 mb-4 items-center">
				<div className="relative">
					<Button
						onClick={() => setShowDatePicker(!showDatePicker)}
						size="auto"
						endIcon={<Calendar size={20} />}
						label={
							dateRange.startDate && dateRange.endDate
								? `${formatDateTime(dateRange.startDate.toISOString()).split(' ')[0]} - ${formatDateTime(dateRange.endDate.toISOString()).split(' ')[0]}`
								: t('SelectDateRange')
						}
					/>

					<span className="ml-5">
						{dateRange.startDate && (
							<Button onClick={handleClearDateRange} size="auto" textOnly label={t('ClearDates')} />
						)}
					</span>

					{showDatePicker && (
						<div className="absolute z-50 mt-2 bg-white shadow-lg rounded-lg">
							<DateRange
								showMonthAndYearPickers={true}
								showDateDisplay={false}
								ranges={[dateRange]}
								months={2}
								onChange={handleDateRangeChange}
								locale={es}
								moveRangeOnFirstSelection={false}
								direction="horizontal"
								rangeColors={[BRAND_COLORS.brand600]}
								minDate={new Date(2020, 0, 1)}
								maxDate={new Date()}
							/>
						</div>
					)}
				</div>

				{(searchInput ||
					selectedStatuses.length > 0 ||
					dateRange.startDate ||
					selectedOnboardingStatuses.length > 0) && (
					<Button
						onClick={handleClearAllFilters}
						size="auto"
						textOnly
						data-testid="clear-all-filters-button"
						label={t('ClearAllFilters')}
					/>
				)}
			</div>

			<div className="custom-scrollbar max-h-[50vh] overflow-y-auto">
				{isInitialLoading ? (
					<div className="flex justify-center items-center h-64">
						<div className="loader" data-testid="loader" />
					</div>
				) : (
					<>
						<Table
							dataTestId="MicrocreditRequestsTable"
							columns={columns}
							data={microcreditRequests}
						/>

						{hasMore && (
							<div className="text-center py-4">
								<Button
									onClick={handleLoadMore}
									size="auto"
									label={t('LoadMore')}
									disabled={loading}
								/>
							</div>
						)}

						{!loading && microcreditRequests.length === 0 && (
							<div className="text-center py-4 text-gray-500">{t('NoResults')}</div>
						)}
					</>
				)}
			</div>

			{loading && !isInitialLoading && (
				<div className="flex justify-center py-4">
					<div className="loader" />
				</div>
			)}

			{!isInitialLoading && totalCount > 0 && (
				<div className="mt-1 ml-5 border-t border-b py-3">
					<Typography size="sm" color={GRAY_COLORS.gray600}>
						{t('ShowingResults')}{' '}
						<span className="font-medium">
							{microcreditRequests.length.toLocaleString()} {t('Of')} {totalCount.toLocaleString()}
						</span>
					</Typography>
				</div>
			)}
		</div>
	);
};

export default MicrocreditRequestsTable;
